import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { catchError, from, map, Observable, of, shareReplay } from "rxjs";
import { ApiService } from "src/app/services/api.service";
import { DialogService } from "src/app/services/dialog.service";
import { friendlyApiErrorMessage } from "src/app/services/api-error-notifier.service";
import { EmailService } from "src/app/services/email-service";
import {
  CaseStage,
  CaseTypeOption,
  EntrySignalGroup,
  EntrySignalOption,
  ReasonCodeOption,
  TriggerConditionOption,
  WfDataSignal,
  WfTemplate,
  WorkflowDefinition,
} from "../page/s09-workflow/s09-workflow.models";

export { WorkflowDefinition } from "../page/s09-workflow/s09-workflow.models";

export interface FieldOption {
  label: string;
  value: any;
}

@Injectable({
  providedIn: "root",
})
export class WorkflowService {
  private statuses$?: Observable<FieldOption[]>;
  private priorities$?: Observable<FieldOption[]>;
  private taskTypes$?: Observable<FieldOption[]>;
  private eventTypes$?: Observable<FieldOption[]>;
  private emailTemplatesByBrand: Map<number, Observable<FieldOption[]>> = new Map();

  private useMockData = false;
  private rawStatuses: any[] = [];
  private rawPriorities: any[] = [];
  private rawTypes: any[] = [];

  getRawStatuses(): any[] { return this.rawStatuses; }
  getRawPriorities(): any[] { return this.rawPriorities; }
  getRawTypes(): any[] { return this.rawTypes; }

  constructor(
    private http: HttpClient,
    private apiService: ApiService,
    private dialogService: DialogService,
    private emailService: EmailService
  ) {}

  private handleError<T>(op: string, err: any, fallback: T): Observable<T> {
    console.error(`[WorkflowService] ${op} failed:`, err);
    this.dialogService.SnackBar(
      `${op} failed. ${friendlyApiErrorMessage(err)}`,
      "Close"
    );
    return of(fallback);
  }

  getTaskStatuses(): Observable<FieldOption[]> {
    if (!this.statuses$) {
      if (this.useMockData) {
        this.statuses$ = of([
          { label: "Todo", value: "todo" },
          { label: "In Progress", value: "inprogress" },
          { label: "Blocked", value: "blocked" },
          { label: "Done", value: "done" },
        ]).pipe(shareReplay(1));
      } else {
        this.statuses$ = this.apiService.post("api/task-statuses/get-all", {}).pipe(
          map((res: any) => {
            this.rawStatuses = this.extractItems(res);
            return this.toFieldOptions(res, "status_name", "task_status_id");
          }),
          catchError(() => of([])),
          shareReplay(1)
        );
      }
    }
    return this.statuses$;
  }

  getTaskPriorities(): Observable<FieldOption[]> {
    if (!this.priorities$) {
      this.priorities$ = this.apiService.post("api/task-priorities/get-all", {}).pipe(
        map((res: any) => {
          this.rawPriorities = this.extractItems(res);
          return this.toFieldOptions(res, "priority_name", "task_priority_id");
        }),
        catchError(() => of([])),
        shareReplay(1)
      );
    }
    return this.priorities$;
  }

  getTaskTypes(): Observable<FieldOption[]> {
    if (!this.taskTypes$) {
      this.taskTypes$ = this.apiService.post("api/task-types/get-all", {}).pipe(
        map((res: any) => {
          this.rawTypes = this.extractItems(res);
          return this.toFieldOptions(res, "type_name", "task_type_id");
        }),
        catchError(() => of([])),
        shareReplay(1)
      );
    }
    return this.taskTypes$;
  }

  getEmailTemplatesForBrand(brandKey: number): Observable<FieldOption[]> {
    const cached = this.emailTemplatesByBrand.get(brandKey);
    if (cached) return cached;
    const stream$ = from(this.emailService.getExtractEmailTemplates(brandKey)).pipe(
      map((res: any) => {
        const items = Array.isArray(res) ? res : this.extractItems(res);
        return items.map((item: any) => ({
          label: item?.template_name ?? "",
          value: item?.email_template_key ?? null,
        }));
      }),
      catchError((err) => this.handleError<FieldOption[]>("Load email templates", err, [])),
      shareReplay(1)
    );
    this.emailTemplatesByBrand.set(brandKey, stream$);
    return stream$;
  }

  getDefinitions(filters?: any): Observable<WorkflowDefinition[]> {
    return this.apiService.post("api/workflows/get-all", filters || {}).pipe(
      map((res: any) => this.extractItems(res) as WorkflowDefinition[]),
      catchError((err) =>
        this.handleError("Load workflows", err, [] as WorkflowDefinition[])
      )
    );
  }

  createDefinition(payload: {
    wf_name: string;
    description?: string;
    created_by?: string;
    trigger_type?: "schedule" | "event" | "manual";
  }): Observable<WorkflowDefinition | null> {
    return this.apiService.post("api/workflows/create", payload).pipe(
      map((res: any) => {
        if (res?.data?.wf_def_id) return res.data as WorkflowDefinition;
        return res as WorkflowDefinition;
      }),
      catchError((err) =>
        this.handleError<WorkflowDefinition | null>("Create workflow", err, null)
      )
    );
  }

  loadFull(wfDefId: number): Observable<any> {
    return this.apiService.post("api/workflows/load-full", { wf_def_id: wfDefId }).pipe(
      map((res: any) => res),
      catchError((err) => this.handleError("Load workflow", err, null))
    );
  }

  saveFull(wfDefId: number, payload: any): Observable<any> {
    return this.apiService
      .post("api/workflows/save-full", { wf_def_id: wfDefId, ...payload })
      .pipe(
        map((res: any) => res),
        catchError((err) => this.handleError("Save workflow", err, null))
      );
  }

  activateDefinition(wfDefId: number, activatedBy: string): Observable<any> {
    return this.apiService
      .post("api/workflows/activate", {
        wf_def_id: wfDefId,
        activated_by: activatedBy,
      })
      .pipe(
        map((res: any) => res),
        catchError((err) => this.handleError("Activate workflow", err, null))
      );
  }

  pauseDefinition(wfDefId: number): Observable<any> {
    return this.apiService.post("api/workflows/pause", { wf_def_id: wfDefId }).pipe(
      map((res: any) => res),
      catchError((err) => this.handleError("Pause workflow", err, null))
    );
  }

  updateDefinition(payload: any): Observable<any> {
    return this.apiService.post("api/workflows/update", payload).pipe(
      map((res: any) => res),
      catchError((err) => this.handleError("Update workflow", err, null))
    );
  }

  deleteDefinition(wfDefId: number): Observable<any> {
    return this.apiService.post("api/workflows/delete", { wf_def_id: wfDefId }).pipe(
      map((res: any) => res),
      catchError((err) => this.handleError("Delete workflow", err, null))
    );
  }

  getRuns(filters?: any): Observable<any[]> {
    return this.apiService.post("api/workflow-runs/get-all", filters || {}).pipe(
      map((res: any) => this.extractItems(res)),
      catchError((err) => this.handleError("Load workflow runs", err, [] as any[]))
    );
  }

  getRunStepLogs(wfRunId: number): Observable<any[]> {
    return this.apiService
      .post("api/workflow-runs/step-logs/get", { wf_run_id: wfRunId })
      .pipe(
        map((res: any) => this.extractItems(res)),
        catchError((err) =>
          this.handleError("Load run step logs", err, [] as any[])
        )
      );
  }

  cancelRun(wfRunId: number): Observable<any> {
    return this.apiService.post("api/workflow-runs/cancel", { wf_run_id: wfRunId }).pipe(
      map((res: any) => res),
      catchError((err) => this.handleError("Cancel run", err, null))
    );
  }

  runCandidates(wfDefId: number, brandKey: number): Observable<any> {
    return this.apiService
      .post("api/workflows/run/candidates", { wf_def_id: wfDefId, brand_key: brandKey })
      .pipe(
        map((res: any) => res?.data || res),
        catchError((err) => this.handleError("Load patients", err, null))
      );
  }

  startRun(payload: {
    wf_def_id: number;
    brand_key: number;
    du_patient_id: number;
    mode: "TEST" | "LIVE";
    initiated_by?: string;
  }): Observable<any> {
    return this.apiService.post("api/workflows/run/start", payload).pipe(
      map((res: any) => res?.data || res),
      catchError((err) => this.handleError("Start run", err, null))
    );
  }

  advanceRun(wfRunId: number, action: string, days?: number | null): Observable<any> {
    return this.apiService
      .post("api/workflows/run/advance", { wf_run_id: wfRunId, action, days: days || null })
      .pipe(
        map((res: any) => res?.data || res),
        catchError((err) => this.handleError("Advance run", err, null))
      );
  }

  runStatus(wfRunId: number): Observable<any> {
    return this.apiService.post("api/workflows/run/status", { wf_run_id: wfRunId }).pipe(
      map((res: any) => res?.data || res),
      catchError((err) => this.handleError("Load run", err, null))
    );
  }

  activeRuns(wfDefId: number): Observable<any[]> {
    return this.apiService.post("api/workflows/run/active", { wf_def_id: wfDefId, brand_key: 0 }).pipe(
      map((res: any) => {
        const body = res?.data || res;
        return body?.items || [];
      }),
      catchError((err) => this.handleError("Load running workflows", err, []))
    );
  }

  getEventTypes(): Observable<FieldOption[]> {
    if (!this.eventTypes$) {
      this.eventTypes$ = this.apiService
        .post("api/workflows/event-types/get-all", {})
        .pipe(
          map((res: any) =>
            this.toFieldOptions(res, "event_type_name", "event_type_key")
          ),
          catchError((err) =>
            this.handleError<FieldOption[]>("Load event types", err, [])
          ),
          shareReplay(1)
        );
    }
    return this.eventTypes$;
  }

  getStepTypes(): Observable<any[]> {
    return this.apiService.post("api/workflows/step-types/get-all", {}).pipe(
      map((res: any) => this.extractItems(res)),
      catchError(() => of([]))
    );
  }

  /** Published + draft case types from api/case-types/list */
  getCaseTypes(brandKey: number, includeDrafts = false): Observable<CaseTypeOption[]> {
    if (!brandKey) return of([]);
    return this.apiService
      .post("api/case-types/list", { brandKey, includeDrafts })
      .pipe(
        map((res: any) => {
          const items = this.extractItems(res);
          return items
            .filter((t: any) => t && (includeDrafts || t.status === "published"))
            .map(
              (t: any): CaseTypeOption => ({
                typeKey: Number(t.typeKey),
                typeCode: String(t.typeCode || ""),
                name: String(t.name || t.typeCode || ""),
                status: t.status,
                slaLabel: t.slaLabel,
              })
            );
        }),
        catchError((err) =>
          this.handleError<CaseTypeOption[]>("Load case types", err, [])
        )
      );
  }

  /** Stages for one case type (DU_ANA_WF_CASE_STAGE). */
  getCaseFlow(brandKey: number, caseTypeKey: number): Observable<CaseStage[]> {
    if (!brandKey || !caseTypeKey) return of([]);
    return this.apiService
      .post("api/workflows/case-flow/get", {
        brand_key: brandKey,
        case_type_key: caseTypeKey,
      })
      .pipe(
        map((res: any) =>
          this.extractItems(res)
            .map(
              (s: any, i: number): CaseStage => ({
                stage_id:
                  s.stage_id != null || s.stageId != null
                    ? Number(s.stage_id ?? s.stageId)
                    : undefined,
                brand_key: Number(s.brand_key ?? s.brandKey ?? brandKey),
                case_type_key: Number(
                  s.case_type_key ?? s.caseTypeKey ?? caseTypeKey
                ),
                stage_name: String(s.stage_name || s.stageName || ""),
                sort_order:
                  s.sort_order != null || s.sortOrder != null
                    ? Number(s.sort_order ?? s.sortOrder)
                    : i,
                is_active: s.is_active !== false && s.is_active !== "N",
                moves_to: this.asNameList(s.moves_to ?? s.movesTo),
              })
            )
            .filter((s) => s.is_active !== false && !!s.stage_name)
            .sort((a, b) => a.sort_order - b.sort_order)
        ),
        catchError((err) =>
          this.handleError<CaseStage[]>("Load case flow", err, [])
        )
      );
  }

  /** Replace the active stage list for one case type. */
  saveCaseFlow(
    brandKey: number,
    caseTypeKey: number,
    stages: { stage_name: string; sort_order: number; moves_to?: string[] }[]
  ): Observable<boolean> {
    return this.apiService
      .post("api/workflows/case-flow/save", {
        brand_key: brandKey,
        case_type_key: caseTypeKey,
        stages,
      })
      .pipe(
        map(() => true),
        catchError((err) =>
          this.handleError<boolean>("Save case flow", err, false)
        )
      );
  }

  /** Entry signal groups from DU_ANA_WF_ENTRY_SIGNAL_* config tables */
  getEntrySignals(brandKey: number): Observable<EntrySignalGroup[]> {
    if (!brandKey) return of([]);
    return this.apiService
      .post("api/workflows/entry-signals/get-all", { brand_key: brandKey })
      .pipe(
        map((res: any) => {
          const items = this.extractItems(res);
          return items.map(
            (g: any): EntrySignalGroup => ({
              group_id: Number(g.group_id),
              group_code: String(g.group_code || ""),
              group_label: String(g.group_label || ""),
              sort_order: g.sort_order,
              signals: (g.signals || []).map((s: any) => ({
                signal_id: Number(s.signal_id),
                signal_code: String(s.signal_code || ""),
                signal_label: s.signal_label,
                sort_order: s.sort_order,
              })),
            })
          );
        }),
        catchError((err) =>
          this.handleError<EntrySignalGroup[]>("Load entry signals", err, [])
        )
      );
  }

  /** Reason codes from DU_ANA_PATIENT_STATUS_DIM for entry-signal picker */
  getReasonCodes(brandKey: number): Observable<ReasonCodeOption[]> {
    if (!brandKey) return of([]);
    return this.apiService
      .post("api/workflows/entry-signals/reason-codes/get-all", {
        brand_key: brandKey,
      })
      .pipe(
        map((res: any) => {
          const items = this.extractItems(res);
          return items
            .map(
              (r: any): ReasonCodeOption => ({
                reason_code: String(r.reason_code || r.PATIENT_REASON_CODE || "").trim(),
                reason_desc:
                  r.reason_desc != null
                    ? String(r.reason_desc)
                    : r.PATIENT_REASON_CODE_DESC != null
                    ? String(r.PATIENT_REASON_CODE_DESC)
                    : "",
              })
            )
            .filter((r) => !!r.reason_code);
        }),
        catchError((err) =>
          this.handleError<ReasonCodeOption[]>("Load reason codes", err, [])
        )
      );
  }

  saveEntrySignalGroup(payload: {
    brand_key: number;
    group_id?: number;
    group_code: string;
    group_label: string;
    sort_order?: number;
    is_active?: boolean;
  }): Observable<EntrySignalGroup | null> {
    return this.apiService
      .post("api/workflows/entry-signals/groups/save", { ...payload })
      .pipe(
        map((res: any) => {
          const g = res?.data || res;
          if (!g?.group_id && !g?.group_code) return null;
          return {
            group_id: Number(g.group_id),
            group_code: String(g.group_code || payload.group_code),
            group_label: String(g.group_label || payload.group_label),
            sort_order: g.sort_order != null ? Number(g.sort_order) : 0,
            signals: g.signals || [],
          } as EntrySignalGroup;
        }),
        catchError((err) =>
          this.handleError<EntrySignalGroup | null>(
            "Save entry signal group",
            err,
            null
          )
        )
      );
  }

  deleteEntrySignalGroup(
    brandKey: number,
    groupId: number
  ): Observable<boolean> {
    return this.apiService
      .post("api/workflows/entry-signals/groups/delete", {
        brand_key: brandKey,
        group_id: groupId,
      })
      .pipe(
        map(() => true),
        catchError((err) =>
          this.handleError<boolean>("Delete entry signal group", err, false)
        )
      );
  }

  saveEntrySignal(payload: {
    brand_key: number;
    group_id: number;
    signal_id?: number;
    signal_code: string;
    signal_label?: string;
    sort_order?: number;
    is_active?: boolean;
  }): Observable<EntrySignalOption | null> {
    return this.apiService
      .post("api/workflows/entry-signals/signals/save", { ...payload })
      .pipe(
        map((res: any) => {
          const s = res?.data || res;
          if (!s?.signal_id && !s?.signal_code) return null;
          return {
            signal_id: Number(s.signal_id),
            signal_code: String(s.signal_code || payload.signal_code),
            signal_label: String(s.signal_label || payload.signal_label || ""),
            sort_order: s.sort_order != null ? Number(s.sort_order) : 0,
          } as EntrySignalOption;
        }),
        catchError((err) =>
          this.handleError<EntrySignalOption | null>(
            "Save entry signal",
            err,
            null
          )
        )
      );
  }

  deleteEntrySignal(brandKey: number, signalId: number): Observable<boolean> {
    return this.apiService
      .post("api/workflows/entry-signals/signals/delete", {
        brand_key: brandKey,
        signal_id: signalId,
      })
      .pipe(
        map(() => true),
        catchError((err) =>
          this.handleError<boolean>("Delete entry signal", err, false)
        )
      );
  }

  /** Condition / threshold options from DU_ANA_WF_TRIGGER_CONDITION */
  getTriggerConditions(
    brandKey: number,
    triggerType?: string
  ): Observable<TriggerConditionOption[]> {
    if (!brandKey) return of([]);
    const body: any = { brand_key: brandKey };
    if (triggerType) body.trigger_type = triggerType;
    return this.apiService
      .post("api/workflows/trigger-conditions/get-all", body)
      .pipe(
        map((res: any) => {
          const items = this.extractItems(res);
          return items.map(
            (c: any): TriggerConditionOption => ({
              condition_id: Number(c.condition_id),
              trigger_type: String(c.trigger_type || ""),
              condition_code: String(c.condition_code || ""),
              condition_label: String(c.condition_label || ""),
              sort_order: c.sort_order,
            })
          );
        }),
        catchError((err) =>
          this.handleError<TriggerConditionOption[]>(
            "Load trigger conditions",
            err,
            []
          )
        )
      );
  }

  /** Templates from DU_ANA_WF_TEMPLATE */
  getTemplates(brandKey: number): Observable<WfTemplate[]> {
    if (!brandKey) return of([]);
    return this.apiService
      .post("api/workflows/templates/get-all", { brand_key: brandKey })
      .pipe(
        map((res: any) => {
          const items = this.extractItems(res);
          return items.map(
            (t: any): WfTemplate => ({
              template_id: t.template_id != null ? Number(t.template_id) : undefined,
              brand_key: t.brand_key != null ? Number(t.brand_key) : brandKey,
              template_code: String(t.template_code || t.id || ""),
              template_name: String(t.template_name || t.name || ""),
              kind: String(t.kind || "Email"),
              source_status: String(t.source_status || t.source || "draft"),
              subject: t.subject != null ? String(t.subject) : "",
              body: t.body != null ? String(t.body) : "",
              sort_order: t.sort_order != null ? Number(t.sort_order) : 0,
              is_active: t.is_active !== false && t.is_active !== "N",
              id: String(t.template_code || t.id || ""),
              name: String(t.template_name || t.name || ""),
            })
          );
        }),
        catchError((err) =>
          this.handleError<WfTemplate[]>("Load templates", err, [])
        )
      );
  }

  saveTemplate(
    payload: Partial<WfTemplate> & { brand_key: number; template_code: string }
  ): Observable<WfTemplate | null> {
    // Use template_body in transit? No — API DTO field is `body`.
    // Clone so apiService.post Object.assign does not mutate caller state.
    const req = {
      brand_key: Number(payload.brand_key),
      template_code: String(payload.template_code || "").trim(),
      template_name: String(
        payload.template_name || payload.name || payload.template_code || ""
      ).trim(),
      kind: payload.kind || "Email",
      source_status: payload.source_status || "draft",
      subject: payload.subject ?? "",
      body: payload.body ?? "",
      sort_order:
        payload.sort_order != null && payload.sort_order !== undefined
          ? Number(payload.sort_order)
          : 0,
      is_active: payload.is_active !== false,
    };
    return this.apiService.post("api/workflows/templates/save", { ...req }).pipe(
      map((res: any) => {
        const t = res?.data || res?.item || res?.result || res;
        const code = t?.template_code || t?.templateCode || req.template_code;
        const id = t?.template_id ?? t?.templateId;
        if (!code && id == null) return null;
        return {
          template_id: id != null ? Number(id) : undefined,
          brand_key:
            t?.brand_key != null || t?.brandKey != null
              ? Number(t.brand_key ?? t.brandKey)
              : req.brand_key,
          template_code: String(code),
          template_name: String(
            t?.template_name || t?.templateName || req.template_name
          ),
          kind: String(t?.kind || req.kind),
          source_status: String(
            t?.source_status || t?.sourceStatus || req.source_status
          ),
          subject:
            t?.subject != null ? String(t.subject) : req.subject,
          body: t?.body != null ? String(t.body) : req.body,
          sort_order:
            t?.sort_order != null || t?.sortOrder != null
              ? Number(t.sort_order ?? t.sortOrder)
              : req.sort_order,
          is_active: t?.is_active !== false && t?.is_active !== "N",
          id: String(code),
          name: String(
            t?.template_name || t?.templateName || req.template_name
          ),
        } as WfTemplate;
      }),
      catchError((err) =>
        this.handleError<WfTemplate | null>("Save template", err, null)
      )
    );
  }

  deleteTemplate(brandKey: number, templateCode: string): Observable<boolean> {
    return this.apiService
      .post("api/workflows/templates/delete", {
        brand_key: brandKey,
        template_code: templateCode,
      })
      .pipe(
        map(() => true),
        catchError((err) =>
          this.handleError<boolean>("Delete template", err, false)
        )
      );
  }

  /** Data-signal dictionary from DU_ANA_WF_DATA_SIGNAL */
  getDataSignals(brandKey: number): Observable<WfDataSignal[]> {
    if (!brandKey) return of([]);
    return this.apiService
      .post("api/workflows/data-signals/get-all", { brand_key: brandKey })
      .pipe(
        map((res: any) => {
          const items = this.extractItems(res);
          return items.map(
            (s: any): WfDataSignal => ({
              data_signal_id:
                s.data_signal_id != null ? Number(s.data_signal_id) : undefined,
              brand_key: s.brand_key != null ? Number(s.brand_key) : brandKey,
              trigger_label: String(s.trigger_label || ""),
              signals_text: String(s.signals_text || ""),
              source_feed: s.source_feed != null ? String(s.source_feed) : "",
              gap_note: s.gap_note != null ? String(s.gap_note) : "",
              sort_order: s.sort_order != null ? Number(s.sort_order) : 0,
            })
          );
        }),
        catchError((err) =>
          this.handleError<WfDataSignal[]>("Load data signals", err, [])
        )
      );
  }

  private asNameList(raw: unknown): string[] | undefined {
    let value = raw;
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) return undefined;
      try {
        value = JSON.parse(trimmed);
      } catch {
        return [trimmed];
      }
    }
    if (!Array.isArray(value)) return undefined;
    const names = value
      .map((item) =>
        typeof item === "string"
          ? item
          : String(
              (item as any)?.stage_name ?? (item as any)?.stageName ?? ""
            )
      )
      .map((name) => name.trim())
      .filter(Boolean);
    return names;
  }

  private extractItems(res: any): any[] {
    if (Array.isArray(res)) return res;
    if (Array.isArray(res?.data)) return res.data;
    if (Array.isArray(res?.items)) return res.items;
    if (Array.isArray(res?.result)) return res.result;
    return [];
  }

  private toFieldOptions(
    res: any,
    labelField: string,
    valueField: string
  ): FieldOption[] {
    return this.extractItems(res).map((item) => ({
      label: item?.[labelField] ?? "",
      value: item?.[valueField] ?? null,
    }));
  }

  GetpatientStatusCodeFilter(
    client_key: number,
    brand_key: string
  ): Observable<any> {
    return this.apiService
      .post("frm-glks/patient-reason-code-filter", {
        client_key,
        brand_key,
      })
      .pipe(
        map((res: any) => res),
        catchError((err) => this.handleError("Get patient status", err, null))
      );
  }
}
